import { ROOF_MEASUREMENT_TYPES } from "../../../../constants/roofMeasurements";
import type { RoofMeasurementKey } from "../../../../interfaces";

export const MEASUREMENT_EDGE_PADDING = { xs: 2, sm: 3, md: 2 } as const;

export function formatMeasurementValue(value: number) {
    if (!value) return "—";
    return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

export type MeasurementRowData = {
    key: RoofMeasurementKey;
    label: string;
    unit: string;
    calculated: number;
    actual: number;
    difference: number;
};

export type VarianceSummaryData = {
    totalCount: number;
    varianceCount: number;
    largestVariance: {
        label: string;
        difference: number;
        unit: string;
    } | null;
};

export function buildMeasurementRows(
    measurements: Record<
        string,
        { Calculated?: number; Actual?: number } | undefined
    >,
): MeasurementRowData[] {
    return ROOF_MEASUREMENT_TYPES.map(({ key, label, unit }) => {
        const calculated = Number(measurements[key]?.Calculated) || 0;
        const actual = Number(measurements[key]?.Actual) || 0;

        return {
            key: key as RoofMeasurementKey,
            label,
            unit,
            calculated,
            actual,
            difference: actual - calculated,
        };
    });
}

export function hasVariance(row: MeasurementRowData) {
    return (row.actual > 0 || row.calculated > 0) && row.difference !== 0;
}

export function buildVarianceSummary(
    rows: MeasurementRowData[],
): VarianceSummaryData {
    const variances = rows.filter(hasVariance);

    let largestVariance: VarianceSummaryData["largestVariance"] = null;

    if (variances.length > 0) {
        const largest = variances.reduce((current, row) =>
            Math.abs(row.difference) > Math.abs(current.difference)
                ? row
                : current,
        );

        largestVariance = {
            label: largest.label,
            difference: largest.difference,
            unit: largest.unit,
        };
    }

    return {
        totalCount: rows.length,
        varianceCount: variances.length,
        largestVariance,
    };
}

export function getDifferenceChipColor(difference: number, hasDiff: boolean) {
    if (!hasDiff) return "neutral" as const;
    if (difference > 0) return "warning" as const;
    if (difference < 0) return "danger" as const;
    return "neutral" as const;
}

export function formatDifferenceLabel(difference: number, hasDiff: boolean) {
    if (!hasDiff) return "0";
    return `${difference > 0 ? "+" : ""}${formatMeasurementValue(difference)}`;
}
